import React, { useEffect, useState } from 'react';
import './WeeklyMatchCountdown.css';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// UK-local wall-clock time, accounting for BST/GMT automatically via
// Intl — suggestionsRounds.hour is always meant as UK local time.
const getUkParts = (date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(date);
  const map = {};
  parts.forEach(p => { map[p.type] = p.value; });
  const weekdayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(map.weekday);
  return { weekdayIndex, hour: parseInt(map.hour, 10), minute: parseInt(map.minute, 10) };
};

// Finds the next occurrence of dayOfWeek/hour:00 UK time, walking forward
// day by day from "now" — avoids ever having to do timezone-offset math
// by hand, since Intl already knows the UK's current DST state.
const getNextOccurrence = (dayOfWeek, hour) => {
  const now = new Date();
  for (let i = 0; i < 8; i++) {
    const candidate = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
    const { weekdayIndex } = getUkParts(candidate);
    if (weekdayIndex === dayOfWeek) {
      const targetUtc = ukLocalToUtc(candidate, dayOfWeek, hour);
      if (targetUtc.getTime() > now.getTime()) {
        return targetUtc;
      }
    }
  }
  // Fallback (shouldn't happen): one week from now at the target hour.
  const fallback = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  fallback.setHours(hour, 0, 0, 0);
  return fallback;
};

// Converts "this calendar date, at `hour`:00 UK local time" into a real
// UTC Date, by comparing UK-formatted wall-clock time against UTC time
// for the same instant to derive the current UK offset.
const ukLocalToUtc = (approxDate, dayOfWeek, hour) => {
  const ukNow = getUkParts(approxDate);
  const utcHour = approxDate.getUTCHours();
  const offsetHours = ukNow.hour - utcHour >= -12 ? ukNow.hour - utcHour : ukNow.hour - utcHour + 24;

  const target = new Date(Date.UTC(
    approxDate.getUTCFullYear(),
    approxDate.getUTCMonth(),
    approxDate.getUTCDate(),
    hour - offsetHours,
    0, 0, 0
  ));
  return target;
};

const getCountdownParts = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const totalMinutes = Math.floor(totalSeconds / 60);
  return {
    days: Math.floor(totalMinutes / (60 * 24)),
    hours: Math.floor((totalMinutes % (60 * 24)) / 60),
    minutes: totalMinutes % 60,
    seconds: totalSeconds % 60,
  };
};

const formatHour = (hour) => {
  if (hour === 0) return '12am';
  if (hour === 12) return 'noon';
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
};

// Weekly-round countdown for 'countdown' mode communities — computes the
// next occurrence of suggestionsRounds.dayOfWeek/hour (UK time) client-side,
// so there's a single source of truth (the stored day/hour) shared with
// whatever real scheduling job eventually runs the actual matching.
const WeeklyMatchCountdown = ({ dayOfWeek, hour, light = false, hideTimer = false, label = '' }) => {
  const [target, setTarget] = useState(() => getNextOccurrence(dayOfWeek, hour));
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    setTarget(getNextOccurrence(dayOfWeek, hour));
  }, [dayOfWeek, hour]);

  useEffect(() => {
    if (hideTimer) return; // no ticking needed if we're not showing the timer
    const interval = setInterval(() => {
      const currentNow = new Date();
      setNow(currentNow);
      if (currentNow.getTime() >= target.getTime()) {
        setTarget(getNextOccurrence(dayOfWeek, hour));
      }
    }, 1000); // 1s tick, so the seconds box actually ticks live
    return () => clearInterval(interval);
  }, [target, dayOfWeek, hour, hideTimer]);

  const remainingMs = target.getTime() - now.getTime();
  const dayLabel = DAY_NAMES[dayOfWeek];
  const hourLabel = formatHour(hour);
  const { days, hours, minutes, seconds } = getCountdownParts(remainingMs);

  return (
    <div className={`weekly-match-countdown${light ? ' weekly-match-countdown--light' : ''}`}>
      {!hideTimer && (
        <div className="weekly-match-countdown-clock">
          <div className="weekly-match-countdown-cell">
            <span className="weekly-match-countdown-number">{days}</span>
            <span className="weekly-match-countdown-unit">days</span>
          </div>
          <div className="weekly-match-countdown-cell">
            <span className="weekly-match-countdown-number">{hours}</span>
            <span className="weekly-match-countdown-unit">hrs</span>
          </div>
          <div className="weekly-match-countdown-cell">
            <span className="weekly-match-countdown-number">{minutes}</span>
            <span className="weekly-match-countdown-unit">min</span>
          </div>
          <div className="weekly-match-countdown-cell">
            <span className="weekly-match-countdown-number">{seconds}</span>
            <span className="weekly-match-countdown-unit">sec</span>
          </div>
        </div>
      )}
      <div className="weekly-match-countdown-label">
        {label || (hideTimer ? `Next round - ${dayLabel} ${hourLabel}` : `until the next round - ${dayLabel} ${hourLabel}`)}
      </div>
    </div>
  );
};

export default WeeklyMatchCountdown;
